import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes = [
  // Public routes
  {
    path: '/',
    name: 'Landing',
    component: () => import('@views/Landing.vue')
  },

  // Auth routes
  {
    path: '/login',
    name: 'EmployeeLogin',
    component: () => import('@views/auth/Login.vue')
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@views/auth/AdminLogin.vue')
  },
  {
    path: '/admin/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@views/auth/ForgotPassword.vue')
  },

  // Admin routes with layout
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, role: 'admin' },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@views/admin/Dashboard.vue')
      },
      {
        path: 'employees',
        name: 'AdminEmployees',
        component: () => import('@views/admin/Employees.vue')
      },
      {
        path: 'attendance',
        name: 'AdminAttendance',
        component: () => import('@views/admin/Attendance.vue')
      },
      {
        path: 'salary',
        name: 'AdminSalary',
        component: () => import('@views/admin/Salary.vue')
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('@views/admin/Settings.vue')
      }
    ]
  },

  // Employee routes with layout
  {
    path: '/employee',
    component: () => import('@/layouts/EmployeeLayout.vue'),
    meta: { requiresAuth: true, role: 'employee' },
    children: [
      {
        path: 'portal',
        name: 'EmployeePortal',
        component: () => import('@views/employee/Portal.vue')
      },
      {
        path: 'checkin',
        name: 'EmployeeCheckin',
        component: () => import('@views/employee/Checkin.vue')
      },
      {
        path: 'history',
        name: 'EmployeeHistory',
        component: () => import('@views/employee/History.vue')
      }
    ]
  },

  // VIP Admin Login (separate, no layout)
  {
    path: '/vipadmin',
    name: 'VipAdminLogin',
    component: () => import('@views/vipadmin/Login.vue')
  },

  // VIP Admin routes with layout
  {
    path: '/vipadmin',
    component: () => import('@/layouts/VipAdminLayout.vue'),
    meta: { requiresAuth: true, role: 'vipadmin' },
    children: [
      {
        path: 'dashboard',
        name: 'VipAdminDashboard',
        component: () => import('@views/vipadmin/Dashboard.vue')
      },
      {
        path: 'companies',
        name: 'VipAdminCompanies',
        component: () => import('@views/vipadmin/Companies.vue')
      },
      {
        path: 'requests',
        name: 'VipAdminRequests',
        component: () => import('@views/vipadmin/Requests.vue')
      },
      {
        path: 'analytics',
        name: 'VipAdminAnalytics',
        component: () => import('@views/vipadmin/Analytics.vue')
      }
    ]
  },

  // Payment routes
  {
    path: '/payment',
    name: 'Payment',
    component: () => import('@views/Payment.vue')
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.meta.requiresAuth || to.matched.some(record => record.meta.requiresAuth)
  const requiredRole = to.meta.role || to.matched.find(record => record.meta.role)?.meta.role

  // Check if route requires authentication
  if (requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Redirect to appropriate login page based on required role
      if (requiredRole === 'vipadmin') {
        return next({ name: 'VipAdminLogin' })
      } else if (requiredRole === 'admin') {
        return next({ name: 'AdminLogin' })
      } else {
        return next({ name: 'EmployeeLogin' })
      }
    }

    // Check role permission
    if (requiredRole && authStore.user?.role !== requiredRole) {
      // User is logged in but doesn't have the right role
      // Redirect to their appropriate dashboard
      const userRole = authStore.user?.role
      if (userRole === 'vipadmin') {
        return next({ name: 'VipAdminDashboard' })
      } else if (userRole === 'admin') {
        return next({ name: 'AdminDashboard' })
      } else if (userRole === 'employee') {
        return next({ name: 'EmployeePortal' })
      } else {
        // Unknown role, logout and go to landing
        authStore.logout()
        return next({ name: 'Landing' })
      }
    }
  }

  // Redirect logged-in users away from login pages
  if (authStore.isAuthenticated) {
    const loginPages = ['EmployeeLogin', 'AdminLogin', 'VipAdminLogin']
    if (loginPages.includes(to.name)) {
      const userRole = authStore.user?.role
      if (userRole === 'vipadmin') {
        return next({ name: 'VipAdminDashboard' })
      } else if (userRole === 'admin') {
        return next({ name: 'AdminDashboard' })
      } else {
        return next({ name: 'EmployeePortal' })
      }
    }
  }

  next()
})

export default router
